import { AlertCircle, Eye, EyeOff, X } from 'lucide-react';
import { useState, type ComponentPropsWithoutRef, type ElementType, type ReactNode } from 'react';

import type { BadgeTone } from '@/lib/status';
import { cn } from '@/lib/utils';

export type { BadgeTone };

/* ==========================================================================
   Admin primitives — denser than the storefront's, tuned for data tables and
   dashboards rather than merchandising.
   ========================================================================== */

// --- Button ----------------------------------------------------------------

type ButtonVariant = 'primary' | 'outline' | 'ghost' | 'danger' | 'success';
type ButtonSize = 'sm' | 'md';

const VARIANTS: Record<ButtonVariant, string> = {
  primary: 'bg-brand-600 text-white hover:bg-brand-700',
  outline:
    'border border-line bg-surface text-ink-muted hover:border-brand-500 hover:text-brand-600',
  ghost: 'text-ink-muted hover:bg-canvas hover:text-ink',
  danger: 'bg-danger-600 text-white hover:bg-danger-700',
  success: 'bg-success-600 text-white hover:bg-success-700',
};

const SIZES: Record<ButtonSize, string> = {
  sm: 'h-8 px-3 text-xs gap-1.5',
  md: 'h-9 px-4 text-sm gap-2',
};

export function Button({
  variant = 'primary',
  size = 'md',
  fullWidth,
  className,
  ...props
}: ComponentPropsWithoutRef<'button'> & {
  variant?: ButtonVariant;
  size?: ButtonSize;
  fullWidth?: boolean;
}) {
  return (
    <button
      className={cn(
        'inline-flex items-center justify-center whitespace-nowrap rounded-lg font-medium transition-colors disabled:pointer-events-none disabled:opacity-50',
        VARIANTS[variant],
        SIZES[size],
        fullWidth && 'w-full',
        className,
      )}
      {...props}
    />
  );
}

// --- Card ------------------------------------------------------------------

export function Card({
  as: Component = 'div',
  className,
  children,
}: {
  as?: ElementType;
  className?: string;
  children: ReactNode;
}) {
  return (
    <Component
      className={cn(
        'rounded-xl border border-line bg-surface shadow-[0_1px_3px_0_rgb(15_23_42_/_0.06)]',
        className,
      )}
    >
      {children}
    </Component>
  );
}

export function CardHeader({
  title,
  subtitle,
  action,
  className,
}: {
  title: string;
  /** Secondary line under the title — a period, a scope, a qualifier. */
  subtitle?: string;
  action?: ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cn(
        'flex items-center justify-between gap-4 border-b border-line px-5 py-3.5',
        className,
      )}
    >
      <div className="min-w-0">
        <h2 className="font-semibold text-ink">{title}</h2>
        {subtitle && <p className="mt-0.5 text-xs text-ink-subtle">{subtitle}</p>}
      </div>
      {action}
    </div>
  );
}

// --- Badge -----------------------------------------------------------------

const TONES: Record<BadgeTone, string> = {
  brand: 'bg-brand-50 text-brand-700',
  success: 'bg-success-50 text-success-700',
  warning: 'bg-warning-50 text-warning-700',
  danger: 'bg-danger-50 text-danger-700',
  neutral: 'bg-canvas text-ink-muted',
  accent: 'bg-accent-50 text-accent-600',
  purple: 'bg-purple-50 text-purple-700',
};

export function Badge({
  tone = 'neutral',
  className,
  children,
}: {
  tone?: BadgeTone;
  className?: string;
  children: ReactNode;
}) {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 whitespace-nowrap rounded-full px-2 py-0.5 text-[11px] font-medium',
        TONES[tone],
        className,
      )}
    >
      {children}
    </span>
  );
}

// --- Stat card -------------------------------------------------------------

export function StatCard({
  label,
  value,
  change,
  changeLabel,
  icon,
  tone = 'brand',
}: {
  label: string;
  value: string | number;
  change?: number;
  changeLabel?: string;
  icon: ReactNode;
  tone?: BadgeTone;
}) {
  return (
    <Card className="p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="truncate text-sm text-ink-subtle">{label}</p>
          <p className="mt-1.5 text-2xl font-bold text-ink">{value}</p>

          {change !== undefined && (
            <p
              className={cn(
                'mt-1.5 flex items-center gap-1 text-xs font-medium',
                change >= 0 ? 'text-success-600' : 'text-danger-600',
              )}
            >
              <span aria-hidden="true">{change >= 0 ? '↑' : '↓'}</span>
              {Math.abs(change).toFixed(1)}%
              {changeLabel && <span className="font-normal text-ink-subtle">{changeLabel}</span>}
            </p>
          )}
        </div>

        <span className={cn('grid size-10 shrink-0 place-items-center rounded-lg', TONES[tone])}>
          {icon}
        </span>
      </div>
    </Card>
  );
}

// --- Table -----------------------------------------------------------------

export function Table({ children, className }: { children: ReactNode; className?: string }) {
  // Wrapper scrolls horizontally so a wide table never forces the page to.
  return (
    <div className="overflow-x-auto">
      <table className={cn('w-full min-w-[720px] text-sm', className)}>{children}</table>
    </div>
  );
}

export function Th({ children, className }: { children?: ReactNode; className?: string }) {
  return (
    <th
      scope="col"
      className={cn(
        'whitespace-nowrap border-b border-line px-4 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wide text-ink-subtle',
        className,
      )}
    >
      {children}
    </th>
  );
}

export function Td({ children, className }: { children?: ReactNode; className?: string }) {
  return (
    <td className={cn('border-b border-line px-4 py-3 align-middle', className)}>{children}</td>
  );
}

// --- Drawer ----------------------------------------------------------------

export function Drawer({
  open,
  onClose,
  title,
  children,
  footer,
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  children: ReactNode;
  footer?: ReactNode;
}) {
  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex justify-end"
      role="dialog"
      aria-modal="true"
      aria-label={title}
    >
      <button
        type="button"
        aria-label="Close panel"
        className="absolute inset-0 bg-overlay"
        onClick={onClose}
      />

      <div className="relative flex h-full w-full max-w-md flex-col bg-surface shadow-2xl">
        <header className="flex items-center justify-between border-b border-line px-5 py-4">
          <h2 className="font-semibold text-ink">{title}</h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close panel"
            className="grid size-8 place-items-center rounded-lg text-ink-subtle hover:bg-canvas hover:text-ink"
          >
            <X className="size-4" aria-hidden="true" />
          </button>
        </header>

        <div className="flex-1 overflow-y-auto p-5">{children}</div>

        {footer && <footer className="border-t border-line p-4">{footer}</footer>}
      </div>
    </div>
  );
}

// --- Form primitives -------------------------------------------------------

const CONTROL_CLASS =
  'w-full rounded-lg border border-line bg-surface px-3 py-2 text-sm text-ink placeholder:text-ink-subtle ' +
  'focus:border-brand-500 focus:outline-none disabled:cursor-not-allowed disabled:bg-canvas disabled:text-ink-subtle';

/**
 * Label, control and error message as one unit.
 *
 * `error` is the server's field error for this input — the API returns them
 * keyed by field name precisely so a form can attach them without guessing.
 */
export function Field({
  label,
  htmlFor,
  hint,
  error,
  required,
  className,
  children,
}: {
  label: string;
  htmlFor?: string;
  hint?: string;
  error?: string;
  required?: boolean;
  className?: string;
  children: ReactNode;
}) {
  return (
    <div className={cn('space-y-1', className)}>
      <label htmlFor={htmlFor} className="block text-xs font-medium text-ink-muted">
        {label}
        {required && (
          <span className="ml-0.5 text-danger-600" aria-hidden="true">
            *
          </span>
        )}
      </label>

      {children}

      {error ? (
        <p className="text-xs text-danger-600">{error}</p>
      ) : (
        hint && <p className="text-xs text-ink-subtle">{hint}</p>
      )}
    </div>
  );
}

export function Input({
  className,
  invalid,
  ...props
}: ComponentPropsWithoutRef<'input'> & { invalid?: boolean }) {
  return (
    <input
      className={cn(CONTROL_CLASS, invalid && 'border-danger-500', className)}
      aria-invalid={invalid || undefined}
      {...props}
    />
  );
}

/**
 * A password field with a show/hide toggle. The toggle is `type="button"` so it
 * never submits the form, and stays focusable so keyboard users can reveal
 * what they typed too.
 */
export function PasswordInput({
  className,
  invalid,
  ...props
}: Omit<ComponentPropsWithoutRef<'input'>, 'type'> & { invalid?: boolean }) {
  const [visible, setVisible] = useState(false);

  return (
    <div className="relative">
      <input
        {...props}
        type={visible ? 'text' : 'password'}
        className={cn(CONTROL_CLASS, 'pr-10', invalid && 'border-danger-500', className)}
        aria-invalid={invalid || undefined}
      />
      <button
        type="button"
        onClick={() => setVisible((current) => !current)}
        className="absolute inset-y-0 right-0 grid w-10 place-items-center rounded-r-lg text-ink-subtle hover:text-ink"
        aria-label={visible ? 'Hide password' : 'Show password'}
        aria-pressed={visible}
        title={visible ? 'Hide password' : 'Show password'}
        disabled={props.disabled}
      >
        {visible ? (
          <EyeOff className="size-4" aria-hidden="true" />
        ) : (
          <Eye className="size-4" aria-hidden="true" />
        )}
      </button>
    </div>
  );
}

export function Textarea({
  className,
  invalid,
  ...props
}: ComponentPropsWithoutRef<'textarea'> & { invalid?: boolean }) {
  return (
    <textarea
      className={cn(CONTROL_CLASS, 'resize-y', invalid && 'border-danger-500', className)}
      aria-invalid={invalid || undefined}
      {...props}
    />
  );
}

export function Select({
  className,
  invalid,
  ...props
}: ComponentPropsWithoutRef<'select'> & { invalid?: boolean }) {
  return (
    <select
      className={cn(CONTROL_CLASS, 'pr-8', invalid && 'border-danger-500', className)}
      aria-invalid={invalid || undefined}
      {...props}
    />
  );
}

export function Checkbox({
  label,
  className,
  ...props
}: ComponentPropsWithoutRef<'input'> & { label: string }) {
  return (
    <label
      className={cn('flex cursor-pointer items-center gap-2 text-sm text-ink-muted', className)}
    >
      <input
        type="checkbox"
        className="size-4 rounded border-line text-brand-600 focus:ring-brand-500"
        {...props}
      />
      {label}
    </label>
  );
}

/** Server-side failure, shown above a form's actions. */
export function ErrorAlert({ title, children }: { title?: string; children: ReactNode }) {
  return (
    <div
      role="alert"
      className="flex items-start gap-2 rounded-lg bg-danger-50 px-3 py-2.5 text-sm text-danger-700"
    >
      <AlertCircle className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
      <div className="min-w-0">
        {title && <p className="font-medium">{title}</p>}
        <div className={cn(title && 'mt-0.5')}>{children}</div>
      </div>
    </div>
  );
}

// --- Misc ------------------------------------------------------------------

export function PageHeader({
  title,
  description,
  action,
}: {
  title: string;
  description?: string;
  action?: ReactNode;
}) {
  return (
    <div className="mb-5 flex flex-wrap items-start justify-between gap-4">
      <div>
        <h1 className="text-xl font-bold tracking-tight text-ink">{title}</h1>
        {description && <p className="mt-0.5 text-sm text-ink-muted">{description}</p>}
      </div>
      {action}
    </div>
  );
}

export function EmptyState({ title, description }: { title: string; description?: string }) {
  return (
    <div className="rounded-xl border border-dashed border-line px-6 py-14 text-center">
      <p className="font-medium text-ink">{title}</p>
      {description && <p className="mt-1 text-sm text-ink-subtle">{description}</p>}
    </div>
  );
}

export function Skeleton({ className }: { className?: string }) {
  return <div className={cn('animate-pulse rounded-lg bg-canvas', className)} aria-hidden="true" />;
}
